/**
 * VarahiID sign-in for Varahi MHI — build-time switches (2026-09-24).
 *
 * The browser never holds a VarahiID token. The access gate (/varahi-mhi-access,
 * access-gate/app.py) is the confidential VarahiID client; it sets path-scoped
 * mhi_session cookies and Caddy forward_auth enforces them on every MHI route.
 * The SPA only asks the gate "who am I" (GET /session) so it can show the user
 * menu, and sends the browser to the gate's /login when that session is gone.
 *
 *   VITE_AUTH_MODE    varahiid | off. Default: varahiid in a production build,
 *                     off under `npm run dev` (offline / Pi demo, no gate).
 *                     off renders the app exactly as before sign-in existed.
 *   VITE_ACCESS_BASE  where the gate is mounted. Default /varahi-mhi-access
 *                     (Caddy in production, the Vite proxy to :8173 in dev).
 *   VITE_VARAHIID_BASE  where VarahiID is mounted. Default /id, same origin.
 */

const MODES = new Set(['varahiid', 'off']);

function resolveMode(raw, isProd) {
  if (raw == null || raw === '') return isProd ? 'varahiid' : 'off';
  const mode = String(raw).trim().toLowerCase();
  if (MODES.has(mode)) return mode;
  // An unknown value fails towards sign-in, never towards "no sign-in". The edge
  // (forward_auth) enforces access either way; this only decides what the SPA shows.
  console.warn(`[auth] unknown VITE_AUTH_MODE ${JSON.stringify(raw)}; using varahiid`);
  return 'varahiid';
}

const stripSlash = (s) => String(s).replace(/\/+$/, '');

export const AUTH_MODE = resolveMode(import.meta.env.VITE_AUTH_MODE, import.meta.env.PROD);
export const AUTH_ENABLED = AUTH_MODE === 'varahiid';
export const ACCESS_BASE = stripSlash(import.meta.env.VITE_ACCESS_BASE || '/varahi-mhi-access');
export const ID_BASE = stripSlash(import.meta.env.VITE_VARAHIID_BASE || '/id');

// VarahiID's shared sign-out page: ends the VarahiID session and pings every
// product's logout (MHI's POST /varahi-mhi-access/logout is in its DEFAULT_PINGS).
// `continue` is ignored by VarahiID today; it is passed anyway, as Happen does,
// so "Sign in again" can return here once VarahiID honours it.
export const SIGNED_OUT_URL = `${ID_BASE}/signed-out`;
export const ACCOUNT_URL = `${ID_BASE}/account`;
export const SWITCHER_SRC = `${ID_BASE}/switcher.js`;

// App root, for `continue` and for reloading after a dev sign-out.
export const APP_BASE = import.meta.env.BASE_URL || '/';
export const IS_DEV = !!import.meta.env.DEV;

// How often the SPA re-asks the gate. The gate itself re-checks VarahiID
// (refresh token) at most every 10 minutes, so polling faster buys nothing.
export const SESSION_POLL_MS = 5 * 60 * 1000;
